import axios from 'axios';
import type {
  FollowupStatus,
  MarketingInterest,
  MarketingLeadsParams,
  MarketingLeadsResponse,
  UpdateRemarksRequest,
  UpdateRemarksResponse,
} from '../types';

// Marketing endpoints are served from the same host as the voice AI call impact API.
const BASE_URL = 'https://cp-voice-agent.clickpe.ai:9010';

/** Interest filter options for the work-queue. */
export const INTEREST_OPTIONS: { label: string; value: MarketingInterest }[] = [
  { label: 'Interested', value: 'interested' },
  { label: 'Not Interested', value: 'not_interested' },
  { label: 'All', value: 'all' },
];

/**
 * Product/campaign (stage) options for marketing leads.
 * Seeded with known campaigns; the table also merges in any distinct
 * `stage` values returned by the API so new campaigns appear automatically.
 */
export const MARKETING_STAGE_OPTIONS: { label: string; value: string }[] = [
  { label: 'Axis Card', value: 'axis_card' },
  { label: 'Kiwi Card', value: 'kiwi_card' },
];

/** Follow-up status options (the editable field) with friendly labels + colors. */
export type FollowupBadgeVariant =
  | 'success'
  | 'danger'
  | 'warning'
  | 'neutral'
  | 'info'
  | 'deep-green';

export const FOLLOWUP_STATUS_OPTIONS: {
  label: string;
  value: FollowupStatus;
  variant: FollowupBadgeVariant;
}[] = [
  { label: 'Contacted', value: 'contacted', variant: 'info' },
  { label: 'Converted', value: 'converted', variant: 'deep-green' },
  { label: 'Callback', value: 'callback', variant: 'warning' },
  { label: 'Not Reachable', value: 'not_reachable', variant: 'neutral' },
  { label: 'Not Interested', value: 'not_interested', variant: 'danger' },
  { label: 'Dropped', value: 'dropped', variant: 'danger' },
];

export function followupLabel(status: FollowupStatus | null): string {
  if (!status) return 'New';
  const opt = FOLLOWUP_STATUS_OPTIONS.find((o) => o.value === status);
  return opt ? opt.label : status;
}

export function followupVariant(status: FollowupStatus | null): FollowupBadgeVariant {
  if (!status) return 'info';
  const opt = FOLLOWUP_STATUS_OPTIONS.find((o) => o.value === status);
  return opt ? opt.variant : 'neutral';
}

/** Resolve the logged-in agent id for the audit trail (optional but recommended). */
export function getAgentId(): string | undefined {
  return (
    localStorage.getItem('agent_id') ||
    localStorage.getItem('agentId') ||
    undefined
  );
}

/**
 * Extract a human-readable error message from an axios/API error.
 * The backend returns `{ detail: "..." }` for 400/404/500 and a
 * `detail` array for 422 validation failures.
 */
export function extractApiError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((d: { msg?: string }) => d?.msg)
        .filter(Boolean)
        .join('; ') || err.message;
    }
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return 'Something went wrong';
}

export async function fetchMarketingLeads(
  params: MarketingLeadsParams
): Promise<MarketingLeadsResponse> {
  const response = await axios.get<MarketingLeadsResponse>(
    `${BASE_URL}/get-marketing-leads`,
    {
      params: {
        interest: params.interest,
        include_actioned: params.include_actioned,
        stage: params.stage || undefined,
        start_date: params.start_date || undefined,
        end_date: params.end_date || undefined,
        limit: params.limit,
        offset: params.offset,
      },
    }
  );
  return response.data;
}

export async function updateCallRemarks(
  body: UpdateRemarksRequest
): Promise<UpdateRemarksResponse> {
  const response = await axios.post<UpdateRemarksResponse>(
    `${BASE_URL}/update-call-remarks`,
    body
  );
  return response.data;
}
